import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { fromEvent, Observable } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { AuthService } from '@shared/services/auth.service';
import { formatHumanReadableDate } from '@shared/utils/data-utils';
import { DateRange } from '@shared/utils/date-range';
import { ClusterUidAndName } from '@shared/models/cluster';
import { PageRequest, SpringPage } from '@shared/page';
import { TrackingService } from '@shared/services/tracking.service';
import { TrackingEvent } from '@shared/models/tracking';
import { RANGE_PRESETS } from '@shared/services/calendar-data.service';
import { ClusterSelectorComponent } from '../../admin/cluster-selector/cluster-selector.component';
import { DateRangeSelectorDirective } from '@shared/directives/date-range-selector.directive';
import { NgFor, NgIf } from '@angular/common';
import { BadgeOrganizationComponent } from '@shared/components/badge-organization/badge-organization.component';
import { JsonViewer } from '@shared/components/json-viewer/json-viewer.component';
import { NgxPaginationModule } from 'ngx-pagination';
import { DateFormatPipe } from '@shared/pipe/filter.pipe';
import { TranslatePipe } from '@ngx-translate/core';
import { AdminClusterTrackingService } from '@shared/services/admin-cluster-tracking.service';

@Component({
  selector: 'app-journal-admin-cluster',
  templateUrl: './journal-admin-cluster.component.html',
  styleUrls: ['./journal-admin-cluster.component.scss'],
  imports: [
    ClusterSelectorComponent,
    DateRangeSelectorDirective,
    NgFor,
    RouterLink,
    BadgeOrganizationComponent,
    NgIf,
    JsonViewer,
    NgxPaginationModule,
    DateFormatPipe,
    TranslatePipe,
  ],
})
export class JournalAdminClusterComponent implements OnInit, AfterViewInit {
  lang: string;

  adminView = false;
  page = 1;
  pageSize = 20;
  totalItems: number;
  periode: DateRange;
  sub: any;
  trackingEvents: TrackingEventVM[];
  cluster: ClusterUidAndName;
  clusters: ClusterUidAndName[] = [];
  @ViewChild('search') search: ElementRef;
  activeRoute: string;

  readonly isAdmin: boolean;

  constructor(
    private route: ActivatedRoute,
    private auth: AuthService,
    private trackingService: TrackingService,
    private adminClusterTrackingService: AdminClusterTrackingService,
  ) {
    this.isAdmin = this.auth.userIsAdmin();
  }

  ngOnInit() {
    this.lang = this.auth.getCurrentLanguage();
    this.adminView = this.auth.userIsAdmin();

    this.periode = RANGE_PRESETS['7d'];
    this.route.url.forEach((response) => (this.activeRoute = response[0].path));
  }

  ngAfterViewInit(): void {
    if (this.activeRoute === 'admin-cluster') {
      this.route.data.subscribe((data: { clusters: Array<any> }) => {
        this.clusters = data.clusters;
        this.cluster = this.clusters[0];
        console.log('cluster',this.cluster)
        this.getLogs(this.cluster.uid);
      });
    } else if (this.activeRoute === 'activities') {
      this.getAdminLogs();
    }
    fromEvent(this.search.nativeElement, 'keyup')
      .pipe(debounceTime(200))
      .subscribe(() => this.refreshLogs());
  }

  onPeriodEmitted(p: DateRange) {
    this.periode = p;
    this.refreshLogs();
  }

  searchByCluster(cluster: ClusterUidAndName) {
    this.cluster = this.clusters.find((c) => c.uid == cluster.uid);
    this.refreshLogs();
  }

  resetIndex() {
    this.page = 1;
  }

  changePage(evt: any) {
    this.page = evt;
    this.refreshLogs();
  }

  refreshLogs() {
    if (this.activeRoute === 'admin-cluster') {
      this.getLogs(this.cluster.uid);
    } else if (this.activeRoute === 'activities') {
      this.getAdminLogs();
    }
  }

  getLogs(clusterUid: string) {
    if (!clusterUid || !this.periode) {
      return;
    }
    this.loadTrackings((q,p) => this.adminClusterTrackingService.getTrackings(clusterUid, q, p));
  }

  getAdminLogs() {
    this.loadTrackings((q,p) => this.trackingService.getTrackings(q, p));
  }

  loadTrackings(load: (query: any, pageRequest: PageRequest)=>Observable<SpringPage<TrackingEvent>>) {
    this.periode = this.periode.refresh();

    const data = {
      startDate: this.periode.start.toISOString(),
      endDate: this.periode.end.toISOString(),
      filter: this.search.nativeElement.value,
    };

    load(this.updateAdminLogData(data), this.getPageRequest())
      .subscribe({
        next: (response: SpringPage<TrackingEvent>) => {
          if (response.content) {
            this.totalItems = response.totalElements;
            this.trackingEvents = response.content.map((event) => this.toViewModel(event));
          }
        },
      });
  }

  private getPageRequest() {
    return { number: this.page - 1, size: this.pageSize };
  }

  private toViewModel(event: TrackingEvent): TrackingEventVM {
    return {
      ...event,
      record: this.formatDates(event.record),
      recordBefore: this.formatDates(event.recordBefore),
      showDetail: false,
    };
  }

  updateLogData(data: { startDate: string; endDate: string; clusterName?: string }): {
    startDate: string;
    endDate: string;
    actionAuthorDetail?: string;
    action?: string;
    siteTrackedName?: string;
    recordTable?: string;
    actionAuthorDetailParam?: { operator: string };
    actionParam?: { operator: string };
    siteTrackedNameParam?: { operator: string };
    recordTableParam?: { operator: string };
  } {
    if (this.search) {
      data['actionAuthorDetail'] = this.search.nativeElement.value;
      data['action'] = this.search.nativeElement.value;
      data['siteTrackedName'] = this.search.nativeElement.value;
      data['recordTable'] = this.search.nativeElement.value;
      data['actionAuthorDetailParam'] = { operator: '%%' };
      data['actionParam'] = { operator: '%%' };
      data['siteTrackedNameParam'] = { operator: '%%' };
      data['recordTableParam'] = { operator: '%%' };
    }

    return data;
  }

  private updateAdminLogData(data: { startDate: string; endDate: string; filter?: string }) {
    const { startDate, endDate, filter } = data;
    return {
      before: endDate,
      after: startDate,
      filter: filter,
    };
  }

  formatDates(record: { createdAt: string; updatedAt: string }): any {
    if (record?.createdAt) record.createdAt = formatHumanReadableDate(record.createdAt, this.lang, 'long');
    if (record?.updatedAt) record.updatedAt = formatHumanReadableDate(record.updatedAt, this.lang, 'long');
    return record;
  }
}

type TrackingEventVM = TrackingEvent & {
  showDetail: boolean;
};
